import { useState, useEffect } from 'react';
import axios from 'axios';
import { Webhook, Copy, Check, RefreshCw, ExternalLink, BookOpen } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STEPS = [
  ['Acesse o painel', 'mercadopago.com.br/developers/panel/app e abra a aplicação do seu Access Token'],
  ['Menu Webhooks', 'Escolha o modo Produção'],
  ['Cole a URL', 'Use a URL ao lado, sem barra no final, sempre com https'],
  ['Marque o evento', 'Apenas "Pagamentos" (payment)'],
  ['Salve e simule', 'Clique em Simular e veja o evento aparecer aqui embaixo'],
];

const AdminWebhookPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const webhookUrl = `${BACKEND_URL}/api/webhooks/mercadopago`;

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/webhooks/mercadopago`, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch (_) {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(webhookUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) { /* clipboard indisponível */ }
  };

  const fmt = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR') : '—');

  return (
    <div className="bg-white rounded-lg shadow mb-8" data-testid="admin-webhook-panel">
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center">
          <Webhook className="h-5 w-5 mr-2 text-sky-600" />
          Webhook Mercado Pago
          {data && (
            <span className={`ml-3 text-xs px-2 py-0.5 rounded-full font-medium ${data.total_received > 0 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`} data-testid="webhook-status-badge">
              {data.total_received > 0 ? 'Ativo — recebendo eventos' : 'Aguardando primeiro evento'}
            </span>
          )}
        </h2>
        <div className="flex items-center gap-2">
          <a href={`${BACKEND_URL}/api/manuais/visualizar/guia-webhook-mercadopago`} target="_blank" rel="noopener noreferrer" className="text-xs flex items-center gap-1 text-blue-600 hover:underline" data-testid="webhook-guide-link">
            <BookOpen className="h-3.5 w-3.5" /> Guia completo
          </a>
          <button onClick={load} className="p-1.5 rounded hover:bg-gray-100" title="Atualizar" data-testid="webhook-refresh-btn">
            <RefreshCw className={`h-4 w-4 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="p-6 grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 space-y-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">URL para cadastrar no Mercado Pago</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-sm bg-gray-900 text-green-300 rounded px-3 py-2 break-all" data-testid="webhook-url">{webhookUrl}</code>
              <button onClick={copy} className="px-3 py-2 rounded bg-sky-600 hover:bg-sky-700 text-white text-sm flex items-center gap-1" data-testid="webhook-copy-btn">
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} {copied ? 'Copiado' : 'Copiar'}
              </button>
            </div>
          </div>

          <ol className="space-y-2">
            {STEPS.map(([title, desc], i) => (
              <li key={i} className="flex gap-3 text-sm">
                <span className="flex-shrink-0 h-6 w-6 rounded-full bg-sky-100 text-sky-700 text-xs font-bold flex items-center justify-center">{i + 1}</span>
                <div><span className="font-medium text-gray-900">{title}</span> <span className="text-gray-600">— {desc}</span></div>
              </li>
            ))}
          </ol>
          <a href="https://www.mercadopago.com.br/developers/panel/app" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-sky-700 hover:underline" data-testid="webhook-mp-panel-link">
            Abrir painel do Mercado Pago <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>

        <div className="lg:col-span-2">
          <div className="grid grid-cols-2 gap-3 mb-4">
            <Stat label="Eventos recebidos" value={data?.total_received ?? '—'} testId="webhook-total-received" />
            <Stat label="PIX pagos via webhook" value={data?.paid_via_webhook ?? '—'} testId="webhook-paid" />
            <Stat label="PIX pendentes" value={data?.pending_pix_payments ?? '—'} testId="webhook-pending" />
            <Stat label="Token MP" value={data ? (data.mp_configured ? 'Configurado' : 'Ausente') : '—'} testId="webhook-mp-configured" />
          </div>
          <p className="text-xs text-gray-500 mb-2">Últimos eventos recebidos</p>
          <div className="border rounded divide-y max-h-56 overflow-y-auto text-xs" data-testid="webhook-recent-events">
            {!data || data.recent_events.length === 0 ? (
              <p className="p-3 text-gray-500">Nenhum evento ainda. Use "Simular" no painel do Mercado Pago.</p>
            ) : data.recent_events.map((ev) => (
              <div key={ev.id} className="p-2 flex justify-between gap-2">
                <span className="text-gray-700">{ev.event_type} <span className="text-gray-400">#{ev.mp_payment_id || '—'}</span></span>
                <span className="text-gray-500 whitespace-nowrap">{fmt(ev.received_at)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const Stat = ({ label, value, testId }) => (
  <div className="rounded border border-gray-200 p-3">
    <p className="text-lg font-bold text-gray-900" data-testid={testId}>{value}</p>
    <p className="text-[11px] text-gray-500">{label}</p>
  </div>
);

export default AdminWebhookPanel;
